<template>
  <div
      @click="editReplacement"
      class="flex flex-col flex-grow flex-shrink-0 p-0 align-top transition-colors duration-150 ease-in-out bg-white border-r border-solid group-hover:bg-neutral-50"
      :class="[
      { 'bg-neutral-100': day && day.year !== currentYear },
      sizeClass,
      { 'cursor-pointer': event },
      eventColorClasses.border,
    ]"
  >
    <div
        v-if="event"
        :class="[eventColorClasses.bg, eventColorClasses.text, 'text-sm overflow-hidden flex flex-col flex-grow']"
        @mouseover="showEventInfo"
        @mouseleave="$emit('hide-event-info')"
    >
      <div class="flex items-center justify-center flex-grow overflow-hidden">
        <template v-if="replacementInitials">
          {{ replacementInitials }}
        </template>
        <AlertCircle
            v-else
            :size="16"
            :class="eventColorClasses.icon"
            title="Ingen afløser sat"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, defineEmits, defineProps} from "vue";
import {AlertCircle} from "lucide-vue-next";

// Define props
const props = defineProps({
  day: {
    type: Object,
    required: true,
  },
  event: {
    type: Object,
    default: null,
  },
  currentYear: {
    type: Number,
    required: true,
  },
  size: {
    type: String,
    default: "small",
  },
});

// Define emits
const emit = defineEmits([
  "edit-replacement",
  "show-event-info",
  "hide-event-info",
]);

// Computed properties
const sizeClass = computed(() => {
  return props.size === "small" ? "w-9 h-9" : "w-11 h-11";
});

const replacementInitials = computed(() => {
  if (props.event && props.event.replacement && props.event.replacement.email) {
    const email = props.event.replacement.email;
    return email.slice(0, 3).toUpperCase();
  }
  return "";
});

const eventColorClasses = computed(() => {
  if (!props.event || !props.event.type) {
    return {
      bg: "bg-white",
      text: "text-neutral-500",
      border: "border-neutral-200",
      icon: "text-neutral-300",
    };
  }

  switch (props.event.type) {
    case "Samtidighedsferieuger":
      return {
        bg: "bg-green-200",
        text: "text-green-800",
        border: "border-green-300",
        icon: "text-green-400",
      };
    case "Særlige feriedage":
      return {
        bg: "bg-purple-200",
        text: "text-purple-800",
        border: "border-purple-300",
        icon: "text-purple-400",
      };
    case "Søgnedage":
      return {
        bg: "bg-amber-200",
        text: "text-amber-800",
        border: "border-amber-300",
        icon: "text-amber-500",
      };
    case "Friweekender":
      return {
        bg: "bg-pink-200",
        text: "text-pink-800",
        border: "border-pink-300",
        icon: "text-pink-400",
      };
    case "Forlagt tjeneste":
      return {
        bg: "bg-teal-200",
        text: "text-teal-800",
        border: "border-teal-300",
        icon: "text-teal-400",
      };
    case "Kursusvirksomhed":
      return {
        bg: "bg-lime-200",
        text: "text-lime-800",
        border: "border-lime-400",
        icon: "text-lime-500",
      };
    case "Erstatningsfridag":
      return {
        bg: "bg-gray-200",
        text: "text-gray-800",
        border: "border-gray-300",
        icon: "text-gray-400",
      };
    case "Sygdom":
      return {
        bg: "bg-red-200",
        text: "text-red-800",
        border: "border-red-300",
        icon: "text-red-400",
      };
    default:
      return {
        bg: "bg-neutral-200",
        text: "text-neutral-500",
        border: "border-neutral-200",
        icon: "text-neutral-300",
      };
  }
});

// Methods
/**
 * Emit an event to edit the replacement.
 */
function editReplacement() {
  if (props.event) {
    emit("edit-replacement", props.event);
  }
}

/**
 * Emit an event to show event information.
 * @param {Event} domEvent - The DOM event.
 */
function showEventInfo(domEvent) {
  emit("show-event-info", props.event, domEvent.clientX, domEvent.clientY);
}
</script>